// packages/web-ui/src/components/inline-cards/InlineCardSlot.tsx
//
// Story W2-S6 AC #1, AC #7 — pure switch-on-kind dispatcher.
//
// SCOPE: zero logic. Receives the envelope (data-only) plus a `handlers`
// object that the caller has composed for the four card kinds. Renders the
// matching card component or `null` (with console.warn) for unknown kinds.
//
// W2-S6-INTEGRATION CONTRACT — the integration agent's MessageBubble will:
//   1. Read inlineCards via `selectCardsForMessage(messageId)`.
//   2. For each envelope, build a `handlers` object that closes over:
//        - state/views.ts             → onFetchLayout
//        - useOpenResource('views', viewId)  → onOpen
//        - state/chat.ts sendMessage  → clarification onSubmit
//        - composer's upload pipeline → fileUpload onConfirm / onCancelled
//   3. Render <InlineCardSlot envelope={env} handlers={handlers}/>.
//
// The slot does not own a store — it's a dumb router. Tests mount it
// with a hand-built handlers object.

import type { JSX } from 'react';
import { useStore, type StoreApi } from 'zustand';
import type { ReactFlowLayoutJson } from '@tessa-lean/base/api-schemas/index.js';
import type {
  InlineCardEnvelope,
  ResponseViewProposalEnvelopeData,
} from '../../state/inline-cards.js';
import type { ChangesetSlice } from '../../state/changeset.js';
import {
  selectImageCard,
  type ImageViewsSlice,
} from '../../state/image-views.js';
import type { ViewsSlice } from '../../state/views.js';
import { ResponseViewProposalCard } from './ResponseViewProposalCard.js';
import { FileUploadConfirm } from './FileUploadConfirm.js';
import { ClarificationCard } from './ClarificationCard.js';
import { KbChangeTreeShell } from '../kb-change-tree-shell/index.js';
import { ChangesetPreviewCard } from './ChangesetPreviewCard.js';
import { IllustrativeImagePanel } from './IllustrativeImagePanel.js';
import { ConnectionsTableCard } from './ConnectionsTableCard.js';
import { DataTableCard } from './DataTableCard.js';

/**
 * Composed handlers for all four card kinds. The integration agent
 * builds one of these per assistant message and passes it down. Per-kind
 * fields are only invoked when the matching envelope kind is rendered.
 */
export interface InlineCardHandlers {
  /** ResponseViewProposalCard handlers. */
  responseView?: {
    /**
     * Fetch the ReactFlow layout JSON for the view. The card mounts a
     * non-interactive React Flow render via `<MiniReactFlowPreview>`
     * against the server-synthesised composition hierarchy + KB-expanded
     * children.
     */
    onFetchLayout: (viewId: string) => Promise<ReactFlowLayoutJson | null>;
    onOpen: (viewId: string) => void;
    /**
     * Optional explicit-save handler. The server has already written the
     * view to disk by the time the card mounts, so this is an idempotent
     * round-trip — it re-PUTs the current layout and lets the user see a
     * "Saved" confirmation. Tests pass `undefined` to skip the affordance.
     */
    onSave?: (
      viewId: string,
      layout: ReactFlowLayoutJson,
    ) => Promise<{ ok: true } | { ok: false; message: string }>;
    /**
     * MAJ-11 — fetch the raw `view.sysml` for the SysML-source tab. Backs the
     * "View SysML source" affordance on the DETERMINISTIC response-view card
     * (the common Q&A card). Returns the source text or null on error. When
     * omitted the SysML tab shows an "unavailable" state. Production wiring
     * (ChatPanel) loads the file via the files route keyed on the view
     * manifest's `sysmlSourcePath`.
     */
    onFetchSysmlSource?: (viewId: string) => Promise<string | null>;
    /**
     * LV3-S1 — fire the opt-in "Generate LLM visualization" follow-up. The
     * card only invokes this when the envelope's `rasterOffered` +
     * `llmImageEnabled` flags are both set (and a handler is wired). The
     * production handler (ChatPanel) mounts the async illustrative-image
     * skeleton via the image-views slice + POSTs `/views/:viewId/image`. The
     * optional `layoutWishes` carries the offer's free-text layout preference;
     * the server layers it additively over the KB scene.
     */
    onGenerateImage?: (
      viewId: string,
      layoutWishes?: string,
    ) => Promise<{ ok: true } | { ok: false; message: string }>;
    /**
     * LV3-S2 — persist the sticky per-view lead engine (Sketch ⇄ LLM image).
     * Backs the engine switcher + "Back to sketch" control. The production
     * handler (ChatPanel) delegates to the `views` slice's `setLeadEngine`,
     * which optimistically patches `manifest.leadEngine` then PUTs
     * `/views/:viewId/lead-engine`. The switcher only renders when a raster is
     * ready AND this handler is wired, so omitting it keeps the sketch-only /
     * switch-OFF path byte-identical.
     */
    onSetLeadEngine?: (
      viewId: string,
      leadEngine: 'sketch' | 'llm-image',
    ) => Promise<{ ok: true } | { ok: false; message: string }>;
    /**
     * LV3-S3 — the inline "adjust layout" refinement loop. Routes a plain-
     * language layout tweak through the SAME chat send path as a typed message
     * (production: `chat.sendMessage`); the server's LV1-S6 routing owns
     * relayout-vs-escalate. Gated only on the sketch leading + this handler
     * being wired (NOT on `views.llmImage.enabled`), so it works with raster
     * fully OFF. Omitted ⇒ no prompt.
     */
    onAdjustLayout?: (
      viewId: string,
      request: string,
    ) => Promise<{ ok: true } | { ok: false; message: string }>;
    /**
     * The FOCUSED (expand-modal) view's direct edit channel. Unlike
     * `onAdjustLayout` (which routes through chat `sendMessage`), this posts the
     * request straight to the view-scoped diagram agent (`POST
     * /views/:viewId/edit`): it executes against THIS view and re-emits
     * `kb:viewRendered` for an in-place re-render WITHOUT landing a turn in the
     * normal chat. The card prefers this for the expand-modal composer; the
     * inline card prompt keeps using `onAdjustLayout` (the chat path). Omitted ⇒
     * the modal composer falls back to `onAdjustLayout` (back-compat / tests).
     */
    onEditViewContent?: (
      viewId: string,
      request: string,
    ) => Promise<{ ok: true } | { ok: false; message: string }>;
    /**
     * Phase 2 — clear a scoped view ("Show all" reveal). Backs the non-
     * dismissible "N parts hidden" badge's button. Production handler delegates
     * to the `views` slice `clearScope` → `PUT /views/:viewId/scope` (empty
     * scope ⇒ server strips it). Omitted ⇒ the badge renders without a button.
     */
    onClearScope?: (
      viewId: string,
    ) => Promise<{ ok: true } | { ok: false; message: string }>;
    /**
     * LV3-S2 — live root store the response-view slot subscribes to (by
     * `viewId`) to derive the per-view sticky `leadEngine` (from the `views`
     * slice) and the raster-ready surface (`rasterReady` / `rasterImageUrl` /
     * `rasterAlt`, from the `image-views` slice). Threading the store — rather
     * than baking the per-view raster status into the data-only envelope at
     * attach time — keeps the switcher reactive: it appears the moment a raster
     * resolves. Optional: when absent the card renders sketch-only (no
     * switcher), byte-identical to the pre-LV3 path. The same root store the
     * `illustrativeImage` handlers thread.
     */
    store?: StoreApi<ImageViewsSlice & ViewsSlice>;
  };
  /** FileUploadConfirm handlers. */
  fileUpload?: {
    onConfirm: (
      uploadHandle: string,
    ) => Promise<
      | {
          ok: true;
          result: { uploadId: string; contentType: string; size: number };
        }
      | { ok: false; message: string }
    >;
    onCancel: () => void;
    onConfirmed: (result: {
      uploadId: string;
      contentType: string;
      size: number;
    }) => void;
  };
  /** ClarificationCard handlers. */
  clarification?: {
    onSubmit: (reply: string) => void;
  };
  /** KbChangeTreeShell handlers. */
  kbChangeTreeShell?: {
    onOpenDiff: (changesetId: string) => void;
    /**
     * When true, hides the embedded W4-S4 tree (which subscribes to a
     * singleton default store and triggers a network fetch on mount). The
     * integration agent flips this off once the changeset slice is wired
     * into RootStore. Default false in production wiring.
     */
    treeDisabled?: boolean;
    /**
     * W4 fix-pass HIGH-G — optional changeset store. When supplied, the
     * embedded inline `<KbChangeTree mode="inline">` reads from this
     * store instead of constructing its own module-level singleton. The
     * root store (composed in HIGH-A) is the production caller.
     */
    store?: StoreApi<ChangesetSlice>;
  };
  /**
   * IV3-S2 / IV3-S3 — IllustrativeImagePanel handlers. The image-views store
   * carries the live per-card lifecycle (keyed by `imageViewId`); the panel
   * subscribes to it via `useStore`.
   */
  illustrativeImage?: {
    store: StoreApi<ImageViewsSlice>;
    onRequestImageEdit: (
      viewId: string,
      instruction: string,
    ) => Promise<{ ok: true } | { ok: false; message: string }>;
    onOpenSysmlSource: (viewId: string, sysmlSourcePath?: string) => void;
    onExportPng?: (viewId: string) => void;
    /**
     * Fetch the DETERMINISTIC ReactFlow layout for the unified card's "Diagram"
     * tab. Reuses the SAME callback the response-view group uses — the
     * illustrative card's `viewId` equals the response-view `viewId`.
     */
    onFetchLayout?: (viewId: string) => Promise<ReactFlowLayoutJson | null>;
    /**
     * Fetch the raw `view.sysml` for the unified card's "SysML source" tab
     * (INLINE pattern). Reuses the SAME response-view callback. When omitted the
     * SysML tab falls back to the external-open `onOpenSysmlSource` affordance.
     */
    onFetchSysmlSource?: (viewId: string) => Promise<string | null>;
    /**
     * The Diagram tab's DETERMINISTIC "adjust layout" box (separate from the
     * AI-image raster edit). Routes a plain-language layout request to
     * `POST /views/:viewId/relayout`; the card re-fetches the layout on
     * `applied`, or nudges to the AI image on an `expressiveness-gap`
     * escalation. Omitted ⇒ the Diagram tab shows no adjust box.
     */
    onAdjustLayout?: (
      viewId: string,
      request: string,
    ) => Promise<
      | { ok: true; applied: boolean; escalation?: 'expressiveness-gap' }
      | { ok: false; message: string }
    >;
  };
}

export interface InlineCardSlotProps {
  envelope: InlineCardEnvelope;
  handlers: InlineCardHandlers;
  /** Honour `prefers-reduced-motion: reduce`. Default: auto-detect inside cards. */
  reducedMotion?: boolean;
}

export function InlineCardSlot(props: InlineCardSlotProps): JSX.Element | null {
  const { envelope, handlers } = props;
  switch (envelope.kind) {
    case 'response-view-proposal': {
      if (handlers.responseView === undefined) {
        // eslint-disable-next-line no-console -- defensive guard for misconfigured slot
        console.warn(
          `InlineCardSlot: missing 'responseView' handlers for envelope ${envelope.props.viewId}`,
        );
        return null;
      }
      return (
        <ResponseViewProposalSlot
          props={envelope.props}
          handlers={handlers.responseView}
          reducedMotion={props.reducedMotion}
        />
      );
    }
    case 'file-upload-confirm': {
      if (handlers.fileUpload === undefined) {
        // eslint-disable-next-line no-console -- defensive guard for misconfigured slot
        console.warn(
          `InlineCardSlot: missing 'fileUpload' handlers for envelope ${envelope.props.uploadHandle}`,
        );
        return null;
      }
      return <FileUploadConfirm {...envelope.props} {...handlers.fileUpload} />;
    }
    case 'clarification': {
      if (handlers.clarification === undefined) {
        // eslint-disable-next-line no-console -- defensive guard for misconfigured slot
        console.warn(
          `InlineCardSlot: missing 'clarification' handlers for envelope`,
        );
        return null;
      }
      return (
        <ClarificationCard
          {...envelope.props}
          onSubmit={handlers.clarification.onSubmit}
        />
      );
    }
    case 'kb-change-tree-shell': {
      if (handlers.kbChangeTreeShell === undefined) {
        // eslint-disable-next-line no-console -- defensive guard for misconfigured slot
        console.warn(
          `InlineCardSlot: missing 'kbChangeTreeShell' handlers for envelope ${envelope.props.changesetId}`,
        );
        return null;
      }
      return (
        <KbChangeTreeShell
          {...envelope.props}
          onOpenDiff={handlers.kbChangeTreeShell.onOpenDiff}
          treeDisabled={handlers.kbChangeTreeShell.treeDisabled}
          {...(handlers.kbChangeTreeShell.store
            ? { store: handlers.kbChangeTreeShell.store }
            : {})}
        />
      );
    }
    case 'changeset-preview': {
      // Pure data card — pulls its `ReactFlowLayoutJson` from the
      // changeset-previews singleton store. No per-card handlers needed,
      // so the InlineCardHandlers contract stays unchanged.
      return <ChangesetPreviewCard {...envelope.props} />;
    }
    case 'illustrative-image': {
      if (handlers.illustrativeImage === undefined) {
        console.warn(
          `InlineCardSlot: missing 'illustrativeImage' handlers for envelope ${envelope.props.imageViewId}`,
        );
        return null;
      }
      return (
        <IllustrativeImagePanel
          {...envelope.props}
          handlers={handlers.illustrativeImage}
          reducedMotion={props.reducedMotion}
        />
      );
    }
    case 'entity-connections': {
      // Pure data card — renders the complete, KB-derived neighbour table.
      // No per-card handlers needed (the InlineCardHandlers contract is
      // unchanged), matching the `changeset-preview` pattern above.
      return <ConnectionsTableCard {...envelope.props} />;
    }
    case 'data-table': {
      // Generic pure data card — renders a complete, KB-derived {columns, rows}
      // table for the tabular skills (hierarchy / weight / …).
      return <DataTableCard {...envelope.props} />;
    }
    default: {
      // Exhaustive switch — TS will flag if a new kind is added.
      const _exhaustive: never = envelope;
      // eslint-disable-next-line no-console -- defensive runtime guard
      console.warn('InlineCardSlot: unknown envelope kind', _exhaustive);
      return null;
    }
  }
}

/**
 * Response-view slot wrapper. Bridges the data-only envelope + message-shared
 * handlers to the props the `<ResponseViewProposalCard>` needs.
 *
 * STACKED-CARDS MODEL (2026-07 UX ruling): the deterministic React-Flow sketch
 * and the AI raster are ALWAYS two separate stacked cards IN THE CHAT STREAM —
 * the sketch here, the raster in the sibling `<IllustrativeImagePanel>` rendered
 * BELOW it. The raster is NEVER swapped into the sketch card BODY, so this
 * wrapper deliberately does NOT thread the in-card Sketch ⇄ LLM-image switcher
 * (`leadEngine` / `onSetLeadEngine`) nor the auto-escalation rationale
 * (`escalated`) — those would toggle the diagram↔raster inside one inline card,
 * the exact "image replaces the diagram" pattern this model removes.
 *
 * FOCUSED-MODAL EXCEPTION (RB ruling 2026-07-07): the fullscreen expand modal
 * (`<ViewImageModal>`, opened by clicking the preview) DOES host the raster in
 * place, with a "Generate AI image" button + a Diagram ⇄ AI image toggle — same
 * as the Views tab. So this wrapper now ALSO threads the raster URL / alt
 * (`rasterImageUrl` / `rasterAlt`). This is SAFE for the stacked-cards ruling:
 * the inline card BODY only surfaces the raster when `onSetLeadEngine` is wired
 * (via `canSwitchEngine`), and that handler is STILL not threaded here — so the
 * URL is consumed only by the modal, never by the inline card body.
 *
 * The raster presence signals it derives — `rasterReady` / `rasterLoading` —
 * still hide the "Generate an AI visualization" offer once a raster exists / is
 * generating. `useStore` requires a stable store ref; a missing store falls back
 * to an empty store so the hook count stays fixed (Rules of Hooks) and resolves
 * to "no raster".
 */
function ResponseViewProposalSlot({
  props,
  handlers,
  reducedMotion,
}: {
  props: ResponseViewProposalEnvelopeData;
  handlers: NonNullable<InlineCardHandlers['responseView']>;
  reducedMotion?: boolean;
}): JSX.Element {
  const store = handlers.store;
  const imageCard = useStore(
    store ?? FALLBACK_RESPONSE_VIEW_STORE,
    selectImageCard(props.viewId),
  );
  const rasterReady = store !== undefined && imageCard?.status === 'ready';
  const rasterLoading = store !== undefined && imageCard?.status === 'generating';
  // Focused-modal exception (see the doc block above): thread the raster URL +
  // alt so the expand modal can show the AI image in place. Consumed ONLY by
  // `<ViewImageModal>`; the inline card body stays sketch-only (no
  // `onSetLeadEngine`, so `canSwitchEngine` is false).
  const rasterImageUrl = store !== undefined ? imageCard?.imageUrl : undefined;
  const rasterAlt = store !== undefined ? imageCard?.alt : undefined;
  // Terminal-failure signal (error / content-filtered). Threaded to the modal
  // so a modal-initiated generate that fails asynchronously resolves its
  // in-place spinner immediately instead of hanging until the timeout backstop.
  const rasterError =
    store !== undefined &&
    (imageCard?.status === 'error' || imageCard?.status === 'filtered');

  // Drop `escalated` from the envelope spread: the auto-escalation rationale +
  // "Back to sketch" toggle belong to the retired in-card engine switch. The
  // escalated raster still generates and lands in its own sibling card below;
  // the sketch here stays permanently visible, so there is nothing to switch
  // "back" to.
  const { escalated: _omitEscalated, ...cardProps } = props;

  return (
    <ResponseViewProposalCard
      {...cardProps}
      onFetchLayout={handlers.onFetchLayout}
      onOpen={handlers.onOpen}
      {...(handlers.onSave !== undefined ? { onSave: handlers.onSave } : {})}
      {...(handlers.onFetchSysmlSource !== undefined
        ? { onFetchSysmlSource: handlers.onFetchSysmlSource }
        : {})}
      {...(handlers.onGenerateImage !== undefined
        ? { onGenerateImage: handlers.onGenerateImage }
        : {})}
      {...(handlers.onAdjustLayout !== undefined
        ? { onAdjustLayout: handlers.onAdjustLayout }
        : {})}
      {...(handlers.onEditViewContent !== undefined
        ? { onEditViewContent: handlers.onEditViewContent }
        : {})}
      {...(handlers.onClearScope !== undefined
        ? { onClearScope: handlers.onClearScope }
        : {})}
      {...(rasterReady ? { rasterReady: true } : {})}
      {...(rasterLoading ? { rasterLoading: true } : {})}
      {...(rasterImageUrl !== undefined ? { rasterImageUrl } : {})}
      {...(rasterAlt !== undefined ? { rasterAlt } : {})}
      {...(rasterError ? { rasterError: true } : {})}
      reducedMotion={reducedMotion}
    />
  );
}

/**
 * Fallback store for the `ResponseViewProposalSlot` hooks when no live store is
 * threaded (tests / older callers). It exposes empty `imageCardsById` +
 * `viewsList` so the `useStore` selectors resolve to "no raster / no lead" —
 * the sketch-only surface. Module-level so the ref is stable across renders
 * (Rules of Hooks: `useStore` needs a stable store identity).
 */
const FALLBACK_RESPONSE_VIEW_STORE: StoreApi<ImageViewsSlice & ViewsSlice> = {
  getState: () => ({ imageCardsById: {}, viewsList: [] }) as unknown as ImageViewsSlice & ViewsSlice,
  getInitialState: () =>
    ({ imageCardsById: {}, viewsList: [] }) as unknown as ImageViewsSlice & ViewsSlice,
  setState: () => undefined,
  subscribe: () => () => undefined,
} as unknown as StoreApi<ImageViewsSlice & ViewsSlice>;
